import React from 'react';
import { useTranslation } from 'react-i18next';
import { buildTransportRouteLabel } from '../../orders/transportAddressUtils';
import './TransportRouteStops.css';

type Route = ReturnType<typeof buildTransportRouteLabel>;

/**
 * Route of one truck. One loading and one delivery stay on a single line
 * ("Gebze → Słupca"); with several stops the loadings and the deliveries get a
 * captioned line each ("Załadunek Gebze" / "Rozładunek Słupca + Konin"), so the
 * operator sees at a glance which stops are pick-ups and which are drops.
 * Loadings and deliveries are independent lists — never paired.
 *
 * Remaining props and the ref reach the outer span, so the component can be
 * the direct child of an antd Tooltip (it injects hover/focus handlers).
 */
interface TransportRouteStopsProps extends React.HTMLAttributes<HTMLSpanElement> {
  route: Route;
  unknownLabel: string;
}

const TransportRouteStops = React.forwardRef<HTMLSpanElement, TransportRouteStopsProps>(({
  route,
  unknownLabel,
  className,
  ...rest
}, ref) => {
  const { t } = useTranslation(['transport']);
  if (route.label === '-') return <span ref={ref} {...rest} className={className}>{unknownLabel}</span>;
  if (route.origins.length <= 1 && route.destinations.length <= 1) {
    return <span ref={ref} {...rest} className={className}>{route.label}</span>;
  }
  return (
    <span
      ref={ref}
      {...rest}
      className={`transport-route-stops ${className || ''}`}
      data-testid="transport-route-stops"
    >
      <span className="transport-route-stops__line">
        <span className="transport-route-stops__caption">{t('transport:grouping.routeLoading')}</span>
        <span>{route.origins.join(' + ')}</span>
      </span>
      <span className="transport-route-stops__line">
        <span className="transport-route-stops__caption">{t('transport:grouping.routeDelivery')}</span>
        <span>{route.destinations.join(' + ')}</span>
      </span>
    </span>
  );
});

TransportRouteStops.displayName = 'TransportRouteStops';

export default TransportRouteStops;
